import React, { useEffect, useState } from "react";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Plus, Trash2, ChevronLeft, ShieldCheck, Workflow, Lock, Folder, List as ListIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { statusDotClasses, statusPillClasses, initials } from "@/lib/format";
import { STATUS_COLORS, getStatusColor } from "@/lib/colors";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";

function ColorPicker({ value, onChange, disabled }) {
  const current = getStatusColor(value);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          disabled={disabled}
          className={`h-9 px-2.5 rounded-sm border border-neutral-200 bg-white flex items-center gap-2 hover:bg-neutral-50 disabled:opacity-50 transition-colors`}
          data-testid="color-picker-trigger"
        >
          <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${current.pill}`}>
            {current.label}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2 px-1">Status color</div>
        <div className="grid grid-cols-5 gap-1.5">
          {STATUS_COLORS.map((c) => (
            <button
              key={c.key}
              onClick={() => onChange(c.key)}
              className={`group relative w-9 h-9 rounded-sm ${c.pill} hover:scale-110 transition-transform ${value === c.key ? "ring-2 ring-offset-1 ring-neutral-900" : ""}`}
              title={c.label}
              data-testid={`color-swatch-${c.key}`}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function WorkspaceSettingsPage() {
  const { activeWorkspace, tree, users, refreshTree, refreshUsers, refreshWorkspaces } = useWorkspace();
  const [members, setMembers] = useState([]);
  const [selectedListId, setSelectedListId] = useState(null);
  const [list, setList] = useState(null);
  const [editStatuses, setEditStatuses] = useState([]);
  const [editRules, setEditRules] = useState([]);
  const [busy, setBusy] = useState(false);

  // Member management
  const [addUserId, setAddUserId] = useState("");
  const [addRole, setAddRole] = useState("member");

  useEffect(() => {
    if (!activeWorkspace) return;
    api.get(`/workspaces/${activeWorkspace.id}/members`).then(r => setMembers(r.data.members)).catch(() => {});
    refreshUsers();
  }, [activeWorkspace, refreshUsers]);

  useEffect(() => {
    if (!selectedListId) { setList(null); return; }
    api.get(`/lists/${selectedListId}`).then(r => {
      setList(r.data.list);
      setEditStatuses((r.data.list.statuses || []).slice().sort((a, b) => a.order - b.order));
      setEditRules(r.data.list.workflow_rules || []);
    }).catch(() => {});
  }, [selectedListId]);

  if (!activeWorkspace) {
    return <div className="p-10 text-sm text-neutral-500">No workspace selected.</div>;
  }

  const isAdmin = activeWorkspace.my_role === "admin";

  const allLists = tree.folders.flatMap(f => (tree.listsByFolder[f.id] || []).map(l => ({ ...l, _folder: f.name })));

  const addStatus = () => {
    setEditStatuses((s) => [...s, { id: crypto.randomUUID(), name: "New Status", color: "neutral", order: s.length }]);
  };
  const removeStatus = (id) => setEditStatuses((s) => s.filter((x) => x.id !== id).map((x, i) => ({ ...x, order: i })));
  const updateStatus = (id, patch) => setEditStatuses((s) => s.map((x) => x.id === id ? { ...x, ...patch } : x));

  const addRule = () => {
    if (editStatuses.length === 0) { toast.error("Add at least one status first"); return; }
    setEditRules((r) => [...r, {
      from_status: null,
      to_status: editStatuses[editStatuses.length - 1].id,
      require_time_logged: true,
      allowed_from_statuses: null,
    }]);
  };
  const removeRule = (idx) => setEditRules((r) => r.filter((_, i) => i !== idx));
  const updateRule = (idx, patch) => setEditRules((r) => r.map((x, i) => i === idx ? { ...x, ...patch } : x));

  const saveList = async () => {
    if (!list) return;
    setBusy(true);
    try {
      await api.patch(`/lists/${list.id}`, {
        statuses: editStatuses.map((s, i) => ({ ...s, order: i })),
        workflow_rules: editRules,
      });
      toast.success("Workflow saved");
      const { data } = await api.get(`/lists/${list.id}`);
      setList(data.list);
      refreshTree();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const addMember = async () => {
    if (!addUserId) return;
    try {
      await api.post(`/workspaces/${activeWorkspace.id}/members`, { user_id: addUserId, role: addRole });
      toast.success("Member added");
      const r = await api.get(`/workspaces/${activeWorkspace.id}/members`);
      setMembers(r.data.members);
      setAddUserId("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    }
  };

  const removeMember = async (userId) => {
    try {
      await api.delete(`/workspaces/${activeWorkspace.id}/members/${userId}`);
      const r = await api.get(`/workspaces/${activeWorkspace.id}/members`);
      setMembers(r.data.members);
      refreshWorkspaces();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    }
  };

  const memberUserIds = new Set(members.map((m) => m.user.id));
  const candidateUsers = users.filter((u) => !memberUserIds.has(u.id));

  return (
    <div data-testid="settings-page">
      <header className="h-14 border-b border-neutral-200 flex items-center px-6 bg-white/80 backdrop-blur-xl sticky top-0 z-20">
        <Link to="/" className="flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-950 transition-colors" data-testid="settings-back">
          <ChevronLeft className="w-4 h-4" /> Back
        </Link>
        <div className="ml-4">
          <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">Settings</div>
          <h1 className="font-display text-base font-bold leading-none">{activeWorkspace.name}</h1>
        </div>
      </header>

      <div className="p-6 max-w-5xl">
        <Tabs defaultValue="workflow" className="space-y-4">
          <TabsList className="rounded-sm">
            <TabsTrigger value="workflow" data-testid="tab-workflow"><Workflow className="w-3.5 h-3.5 mr-1.5" />Workflow rules</TabsTrigger>
            <TabsTrigger value="members" data-testid="tab-members"><ShieldCheck className="w-3.5 h-3.5 mr-1.5" />Members</TabsTrigger>
            <TabsTrigger value="access" data-testid="tab-access"><Lock className="w-3.5 h-3.5 mr-1.5" />Folder &amp; List access</TabsTrigger>
          </TabsList>

          <TabsContent value="workflow" className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              {/* List picker */}
              <div className="bg-white border border-neutral-200 rounded-sm p-3">
                <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Lists</div>
                <div className="space-y-1">
                  {allLists.length === 0 && <div className="text-xs text-neutral-500 px-2 py-2">Create a list first.</div>}
                  {allLists.map((l) => (
                    <button
                      key={l.id}
                      onClick={() => setSelectedListId(l.id)}
                      className={`w-full text-left px-2 py-1.5 rounded-sm text-sm transition-colors ${
                        selectedListId === l.id ? "bg-neutral-950 text-white" : "hover:bg-neutral-50"
                      }`}
                      data-testid={`settings-list-${l.id}`}
                    >
                      <div className="font-medium truncate">{l.name}</div>
                      <div className={`text-[10px] ${selectedListId === l.id ? "text-neutral-300" : "text-neutral-500"}`}>{l._folder}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Editor */}
              <div className="md:col-span-2 space-y-4">
                {!list ? (
                  <div className="bg-white border border-dashed border-neutral-300 rounded-sm p-10 text-center text-sm text-neutral-500" data-testid="workflow-empty">
                    Select a list to customise its statuses and workflow rules.
                  </div>
                ) : (
                  <>
                    {/* Statuses */}
                    <div className="bg-white border border-neutral-200 rounded-sm">
                      <div className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
                        <div>
                          <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">List</div>
                          <h3 className="font-display font-bold">{list.name} · Statuses</h3>
                        </div>
                        {isAdmin && (
                          <Button onClick={addStatus} variant="outline" className="rounded-sm h-8" data-testid="add-status-button">
                            <Plus className="w-3.5 h-3.5 mr-1" /> Status
                          </Button>
                        )}
                      </div>
                      <div className="divide-y divide-neutral-100">
                        {editStatuses.map((s, i) => (
                          <div key={s.id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`status-row-${s.id}`}>
                            <span className="font-mono-data text-[10px] text-neutral-400 w-5">{i + 1}</span>
                            <span className={`w-2.5 h-2.5 rounded-full ${statusDotClasses(s.color)}`} />
                            <Input
                              value={s.name}
                              disabled={!isAdmin}
                              onChange={(e) => updateStatus(s.id, { name: e.target.value })}
                              className="rounded-sm h-9 flex-1"
                            />
                            <ColorPicker
                              value={s.color}
                              onChange={(v) => updateStatus(s.id, { color: v })}
                              disabled={!isAdmin}
                            />
                            {isAdmin && (
                              <button
                                onClick={() => removeStatus(s.id)}
                                className="text-neutral-400 hover:text-red-600 transition-colors"
                                data-testid={`remove-status-${s.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Workflow Rules */}
                    <div className="bg-white border border-neutral-200 rounded-sm">
                      <div className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
                        <div>
                          <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">Constraints</div>
                          <h3 className="font-display font-bold">Workflow rules</h3>
                        </div>
                        {isAdmin && (
                          <Button onClick={addRule} variant="outline" className="rounded-sm h-8" data-testid="add-rule-button">
                            <Plus className="w-3.5 h-3.5 mr-1" /> Rule
                          </Button>
                        )}
                      </div>

                      {editRules.length === 0 && (
                        <div className="px-4 py-6 text-center text-sm text-neutral-500" data-testid="rules-empty">
                          No rules yet. Add a rule to restrict transitions or require time entry.
                        </div>
                      )}

                      <div className="divide-y divide-neutral-100">
                        {editRules.map((r, idx) => (
                          <div key={idx} className="p-4 space-y-3" data-testid={`rule-row-${idx}`}>
                            <div className="grid md:grid-cols-2 gap-3">
                              <div>
                                <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">Target status</Label>
                                <Select value={r.to_status} onValueChange={(v) => updateRule(idx, { to_status: v })} disabled={!isAdmin}>
                                  <SelectTrigger className="rounded-sm h-9 mt-1" data-testid={`rule-to-${idx}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {editStatuses.map((s) => (
                                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">Allowed from (optional)</Label>
                                <Select
                                  value={r.allowed_from_statuses?.length === 1 ? r.allowed_from_statuses[0] : "__any__"}
                                  onValueChange={(v) => updateRule(idx, { allowed_from_statuses: v === "__any__" ? null : [v] })}
                                  disabled={!isAdmin}
                                >
                                  <SelectTrigger className="rounded-sm h-9 mt-1" data-testid={`rule-from-${idx}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__any__">Any status</SelectItem>
                                    {editStatuses.map((s) => (
                                      <SelectItem key={s.id} value={s.id}>Only from “{s.name}”</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={!!r.require_time_logged}
                                onChange={(e) => updateRule(idx, { require_time_logged: e.target.checked })}
                                disabled={!isAdmin}
                                data-testid={`rule-require-time-${idx}`}
                              />
                              Require time logged before transitioning to this status
                            </label>
                            {isAdmin && (
                              <div className="flex justify-end">
                                <button
                                  onClick={() => removeRule(idx)}
                                  className="text-xs text-red-600 hover:underline"
                                  data-testid={`remove-rule-${idx}`}
                                >
                                  Remove rule
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {isAdmin && (
                      <div className="flex justify-end">
                        <Button onClick={saveList} disabled={busy} className="rounded-sm bg-neutral-950 hover:bg-neutral-800" data-testid="save-workflow-button">
                          {busy ? "Saving…" : "Save workflow"}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="members">
            <div className="bg-white border border-neutral-200 rounded-sm">
              <div className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
                <div>
                  <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">Workspace</div>
                  <h3 className="font-display font-bold">Members</h3>
                </div>
              </div>

              {isAdmin && (
                <div className="px-4 py-3 border-b border-neutral-200 flex flex-wrap gap-2 items-end" data-testid="add-member-row">
                  <div className="flex-1 min-w-[200px]">
                    <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">User</Label>
                    <Select value={addUserId} onValueChange={setAddUserId}>
                      <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="add-member-user-select"><SelectValue placeholder="Select a user" /></SelectTrigger>
                      <SelectContent>
                        {candidateUsers.length === 0 && <div className="px-2 py-2 text-xs text-neutral-500">No more users to add.</div>}
                        {candidateUsers.map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-36">
                    <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">Role</Label>
                    <Select value={addRole} onValueChange={setAddRole}>
                      <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="add-member-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">admin</SelectItem>
                        <SelectItem value="member">member</SelectItem>
                        <SelectItem value="viewer">viewer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={addMember} disabled={!addUserId} className="rounded-sm h-9 bg-neutral-950 hover:bg-neutral-800" data-testid="add-member-button">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add
                  </Button>
                </div>
              )}

              <div className="divide-y divide-neutral-100">
                {members.map((m) => (
                  <div key={m.user.id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`member-row-${m.user.id}`}>
                    <div className="w-7 h-7 rounded-full bg-neutral-950 text-white text-[10px] font-bold flex items-center justify-center">{initials(m.user.name)}</div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">{m.user.name}</div>
                      <div className="text-xs text-neutral-500">{m.user.email}</div>
                    </div>
                    <span className="font-mono-data text-[10px] uppercase tracking-widest px-2 py-1 border border-neutral-300 rounded-full">{m.role}</span>
                    {isAdmin && !m.owner && (
                      <button onClick={() => removeMember(m.user.id)} className="text-neutral-400 hover:text-red-600 transition-colors" data-testid={`remove-member-${m.user.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    {m.owner && <span className="font-mono-data text-[10px] text-neutral-400 uppercase tracking-widest">owner</span>}
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="access">
            <AccessTab activeWorkspace={activeWorkspace} tree={tree} users={users} isAdmin={isAdmin} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function AccessTab({ activeWorkspace, tree, users, isAdmin }) {
  const [scope, setScope] = useState({ kind: null, id: null, name: "" });
  const [members, setMembers] = useState([]);
  const [addUserId, setAddUserId] = useState("");
  const [addRole, setAddRole] = useState("member");

  const userMap = Object.fromEntries(users.map((u) => [u.id, u]));

  const refreshMembers = async () => {
    if (!scope.kind || !scope.id) { setMembers([]); return; }
    try {
      const url = scope.kind === "folder" ? `/folders/${scope.id}/members` : `/lists/${scope.id}/members`;
      const { data } = await api.get(url);
      setMembers(data.members);
    } catch (e) {
      setMembers([]);
    }
  };

  useEffect(() => { refreshMembers(); }, [scope.kind, scope.id]);

  const addMember = async () => {
    if (!addUserId || !scope.id) return;
    try {
      const url = scope.kind === "folder" ? `/folders/${scope.id}/members` : `/lists/${scope.id}/members`;
      await api.post(url, { user_id: addUserId, role: addRole });
      toast.success(`Granted ${addRole} on ${scope.name}`);
      setAddUserId("");
      refreshMembers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to grant");
    }
  };

  return (
    <div className="grid md:grid-cols-3 gap-4">
      <div className="bg-white border border-neutral-200 rounded-sm p-3">
        <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Scope</div>
        <div className="space-y-2">
          {tree.folders.length === 0 && <div className="text-xs text-neutral-500 px-2 py-2">No folders yet.</div>}
          {tree.folders.map((f) => (
            <div key={f.id}>
              <button
                onClick={() => setScope({ kind: "folder", id: f.id, name: f.name })}
                className={`w-full text-left px-2 py-1.5 rounded-sm text-sm flex items-center gap-2 transition-colors ${scope.kind === "folder" && scope.id === f.id ? "bg-neutral-950 text-white" : "hover:bg-neutral-50"}`}
                data-testid={`access-folder-${f.id}`}
              >
                <Folder className="w-3.5 h-3.5" />
                <span className="font-medium truncate">{f.name}</span>
              </button>
              <div className="ml-5 border-l border-neutral-100 pl-2 mt-1 space-y-1">
                {(tree.listsByFolder[f.id] || []).map((l) => (
                  <button
                    key={l.id}
                    onClick={() => setScope({ kind: "list", id: l.id, name: l.name })}
                    className={`w-full text-left px-2 py-1 rounded-sm text-xs flex items-center gap-2 transition-colors ${scope.kind === "list" && scope.id === l.id ? "bg-neutral-950 text-white" : "hover:bg-neutral-50"}`}
                    data-testid={`access-list-${l.id}`}
                  >
                    <ListIcon className="w-3 h-3" />
                    <span className="truncate">{l.name}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="md:col-span-2">
        {!scope.kind ? (
          <div className="bg-white border border-dashed border-neutral-300 rounded-sm p-10 text-center text-sm text-neutral-500" data-testid="access-empty">
            Pick a folder or list on the left to manage who has access at that level.
          </div>
        ) : (
          <div className="bg-white border border-neutral-200 rounded-sm">
            <div className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
              <div>
                <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">{scope.kind === "folder" ? "Folder" : "List"} access</div>
                <h3 className="font-display font-bold">{scope.name}</h3>
              </div>
            </div>

            {isAdmin && (
              <div className="px-4 py-3 border-b border-neutral-200 flex flex-wrap gap-2 items-end" data-testid="access-add-row">
                <div className="flex-1 min-w-[200px]">
                  <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">User</Label>
                  <Select value={addUserId} onValueChange={setAddUserId}>
                    <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="access-user-select"><SelectValue placeholder="Select a user" /></SelectTrigger>
                    <SelectContent>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-36">
                  <Label className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">Role</Label>
                  <Select value={addRole} onValueChange={setAddRole}>
                    <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="access-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">admin</SelectItem>
                      <SelectItem value="member">member</SelectItem>
                      <SelectItem value="viewer">viewer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={addMember} disabled={!addUserId} className="rounded-sm h-9 bg-neutral-950 hover:bg-neutral-800" data-testid="access-add-button">
                  <Plus className="w-3.5 h-3.5 mr-1" />Grant access
                </Button>
              </div>
            )}

            <div className="px-4 py-3 text-xs text-neutral-500 bg-neutral-50 border-b border-neutral-200">
              Members below have <b>explicit</b> access to this {scope.kind}. They also inherit the highest role they hold higher in the tree (workspace → folder → list).
            </div>

            <div className="divide-y divide-neutral-100">
              {members.length === 0 && <div className="px-4 py-6 text-xs text-neutral-500 text-center" data-testid="access-empty-members">No explicit access yet. Inherited from workspace.</div>}
              {members.map((m) => {
                const u = userMap[m.user_id];
                return (
                  <div key={m.id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`access-row-${m.user_id}`}>
                    <div className="w-7 h-7 rounded-full bg-neutral-950 text-white text-[10px] font-bold flex items-center justify-center">{initials(u?.name)}</div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">{u?.name || "Unknown user"}</div>
                      <div className="text-xs text-neutral-500">{u?.email}</div>
                    </div>
                    <span className="font-mono-data text-[10px] uppercase tracking-widest px-2 py-1 border border-neutral-300 rounded-full">{m.role}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
